import { useState, useEffect } from "react";
import { DashboardShell } from "../../components/dashboard/DashboardKit";
import API from "../../services/api";
import {
  Home,
  ClipboardList,
  Star,
  FileText,
  Phone,
  Wrench,
  Paintbrush,
  Mail,
  Users,
} from "lucide-react";

// ─── NAV (unchanged paths/labels/keys — icons now lucide-react) ─────────────
const clientNav = [
  { icon: <Home className="w-5 h-5" />,          label: "Dashboard",        path: "/client-dashboard",    key: "dashboard"    },
  { icon: <ClipboardList className="w-5 h-5" />, label: "Project Timeline", path: "/project-timeline",    key: "timeline"     },
  { icon: <Star className="w-5 h-5" />,          label: "Feedback",         path: "/client-testimonials",  key: "testimonials" },
  { icon: <FileText className="w-5 h-5" />,      label: "Reports",          path: "/client-reports",       key: "reports"      },
  { icon: <Phone className="w-5 h-5" />,         label: "Contact",          path: "/contact",              key: "contact"      },
];

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  // Role accent colours
  engineer:        "#5C2D91",
  engineerLight:   "#EDE6F7",
  engineerMid:     "#D8C8F5",
  supervisor:      "#0EA5E9",
  supervisorLight: "#F0F9FF",
  supervisorMid:   "#BAE6FD",
  applicator:      "#10B981",
  applicatorLight: "#ECFDF5",
  applicatorMid:   "#A7F3D0",
  // Neutrals
  bg:          "#F4F6FB",
  surface:     "#FFFFFF",
  border:      "#E5E7EB",
  borderHover: "#C7D2E8",
  dark:        "#111827",
  body:        "#374151",
  muted:       "#6B7280",
  faint:       "#9CA3AF",
  success:     "#10B981",
  successBg:   "#ECFDF5",
  successBorder:"#A7F3D0",
};

// ─── ROLE CONFIG ─────────────────────────────────────────────────────────────
// Maps a designation string → one of our three roles
const ROLE_MAP = {
  engineer:   { label: "Site Engineer",  color: T.engineer,   light: T.engineerLight,   mid: T.engineerMid,   icon: Wrench,        gradient: "linear-gradient(135deg,#3D1870,#5C2D91)" },
  supervisor: { label: "Supervisor",     color: T.supervisor, light: T.supervisorLight, mid: T.supervisorMid, icon: ClipboardList, gradient: "linear-gradient(135deg,#0369A1,#0EA5E9)" },
  applicator: { label: "Applicator",     color: T.applicator, light: T.applicatorLight, mid: T.applicatorMid, icon: Paintbrush,    gradient: "linear-gradient(135deg,#059669,#10B981)" },
};
function resolveRole(designation = "") {
  const d = (designation || "").trim().toLowerCase();
  if (d.includes("supervisor") || d.includes("site manager")) return "supervisor";
  if (
    d.includes("applicator") || d.includes("painter") ||
    d.includes("worker")    || d.includes("manager") ||
    d.includes("product")   || d.includes("contractor") ||
    d.includes("vendor")    || d.includes("technician")
  ) return "applicator";
  return "engineer"; // default
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const mkInitials = (name) =>
  (name || "").split(" ").map((w) => w[0] || "").join("").toUpperCase().slice(0, 2) || "?";

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

// Avatar circle with role gradient
function RoleAvatar({ name, roleKey, size = 56 }) {
  const cfg = ROLE_MAP[roleKey] || ROLE_MAP.engineer;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: cfg.gradient,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.3, fontWeight: 800, color: "#fff",
      boxShadow: `0 4px 14px ${cfg.color}40`,
      border: `2px solid ${cfg.mid}`,
      letterSpacing: "-0.5px",
    }}>
      {mkInitials(name)}
    </div>
  );
}

// Role badge pill
function RoleBadge({ roleKey }) {
  const cfg = ROLE_MAP[roleKey] || ROLE_MAP.engineer;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: cfg.light,
      border: `1px solid ${cfg.mid}`,
      color: cfg.color,
      fontSize: 10, fontWeight: 700,
      padding: "4px 10px", borderRadius: 20,
      letterSpacing: "0.05em", textTransform: "uppercase",
    }}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
}

// Available badge
function AvailableBadge() {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: T.successBg, border: `1px solid ${T.successBorder}`,
      color: T.success, fontSize: 10, fontWeight: 700,
      padding: "4px 10px", borderRadius: 20,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: T.success, display: "inline-block",
        animation: "pulse 2s infinite",
      }} />
      Available
    </span>
  );
}

// Action link button
function ContactBtn({ icon: Icon, label, href, color, light }) {
  const [hov, setHov] = useState(false);
  return (
    <a href={href}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "8px 16px", borderRadius: 9,
        fontSize: 12, fontWeight: 700, textDecoration: "none",
        transition: "all 0.2s",
        border: `1.5px solid ${hov ? color : T.border}`,
        background: hov ? color : T.surface,
        color: hov ? "#fff" : color,
        boxShadow: hov ? `0 3px 12px ${color}30` : "none",
        transform: hov ? "translateY(-1px)" : "none",
      }}
    >
      <Icon size={14} />
      {label}
    </a>
  );
}

// ── Main contact card ─────────────────────────────────────────────────────────
function ContactCard({ person, roleKey }) {
  const [hov, setHov] = useState(false);
  const cfg = ROLE_MAP[roleKey] || ROLE_MAP.engineer;
  const WatermarkIcon = cfg.icon;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: T.surface,
        border: `1.5px solid ${hov ? cfg.color + "60" : T.border}`,
        borderRadius: 20,
        overflow: "hidden",
        boxShadow: hov ? `0 12px 36px ${cfg.color}18` : "0 2px 10px rgba(0,0,0,0.06)",
        transform: hov ? "translateY(-4px)" : "translateY(0)",
        transition: "all 0.28s cubic-bezier(0.34,1.56,0.64,1)",
        display: "flex", flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Top colour bar */}
      <div style={{
        height: 5,
        background: cfg.gradient,
        opacity: hov ? 1 : 0.7,
        transition: "opacity 0.2s",
      }} />

      {/* Role watermark icon */}
      <div style={{
        position: "absolute", top: 16, right: 16,
        opacity: 0.06, pointerEvents: "none",
        userSelect: "none", lineHeight: 1, color: cfg.color,
      }}>
        <WatermarkIcon size={40} />
      </div>

      <div style={{ padding: "22px 22px 20px", display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>

        {/* Header row: avatar + badges */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <RoleAvatar name={person.name} roleKey={roleKey} size={56} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <AvailableBadge />
            <RoleBadge roleKey={roleKey} />
          </div>
        </div>

        {/* Name + designation */}
        <div>
          <div style={{ fontWeight: 800, fontSize: 18, color: T.dark, marginBottom: 3, letterSpacing: "-0.3px" }}>
            {person.name || "—"}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            {person.designation || cfg.label}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: T.border }} />

        {/* Contact info */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {person.email && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                background: cfg.light, border: `1px solid ${cfg.mid}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: cfg.color,
              }}><Mail size={14} /></div>
              <span style={{
                fontSize: 13, color: T.body, fontWeight: 500,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{person.email}</span>
            </div>
          )}
          {person.phone && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                background: cfg.light, border: `1px solid ${cfg.mid}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: cfg.color,
              }}><Phone size={14} /></div>
              <span style={{ fontSize: 13, color: T.body, fontWeight: 500 }}>{person.phone}</span>
            </div>
          )}
          {!person.email && !person.phone && (
            <div style={{ fontSize: 12, color: T.faint, fontStyle: "italic" }}>No contact details available</div>
          )}
        </div>

{/* Action buttons */}
        {/* <div style={{ display: "flex", gap: 8, marginTop: "auto", paddingTop: 4 }}>
          {person.email && (
            <ContactBtn icon={Mail} label="Email" href={`mailto:${person.email}`} color={cfg.color} light={cfg.light} />
          )}
          {person.phone && (
            <ContactBtn icon={Phone} label="Call" href={`tel:${person.phone}`} color={cfg.color} light={cfg.light} />
          )}
        </div> */}
      </div>
    </div>
  );
}

// ── Empty state card ──────────────────────────────────────────────────────────
function EmptyCard({ roleKey }) {
  const cfg = ROLE_MAP[roleKey] || ROLE_MAP.engineer;
  const Icon = cfg.icon;
  return (
    <div style={{
      background: T.surface,
      border: `1.5px dashed ${T.border}`,
      borderRadius: 20,
      padding: "40px 24px",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 10,
      textAlign: "center", color: T.faint,
    }}>
      <div style={{ opacity: 0.3, color: T.faint }}><Icon size={36} /></div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.muted }}>{cfg.label} not assigned</div>
      <div style={{ fontSize: 12, color: T.faint }}>Will appear here once assigned to this project.</div>
    </div>
  );
}

// ─── KPI STRIP ────────────────────────────────────────────────────────────────
function KpiChip({ icon: Icon, label, value, color, light }) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 14, padding: "16px 20px",
      display: "flex", alignItems: "center", gap: 14,
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 11, flexShrink: 0,
        background: light,
        display: "flex", alignItems: "center", justifyContent: "center",
        color,
      }}><Icon size={20} /></div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>
          {label}
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT — ONLY FETCH URL AND STATE SHAPE UNCHANGED
// ═══════════════════════════════════════════════════════════════════════════════
export default function ContactPage() {
  // ── STATE (unchanged fields) ───────────────────────────────────────────────
const [engineer, setEngineer] = useState(null);
const [stakeholders, setStakeholders] = useState([]);

  const userName     = localStorage.getItem("name") || "User";
  const userRole     = localStorage.getItem("role") || "Client";
  const userInitials = userName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  // ── FETCH (unchanged — same URL, same .then chain) ─────────────────────────
useEffect(() => {
  const projectId = localStorage.getItem("project_id");

  // Fetch stakeholders (supervisor etc.)
  API.get(`/client/stakeholders/${projectId}`)
    .then((res) => {
      setStakeholders(res.data.stakeholders || []);
    })
    .catch(console.error);

  // Fetch engineer from project_assignments
  API.get(`/client/project-engineer/${projectId}`)
    .then((res) => {
      if (res.data.engineer) setEngineer(res.data.engineer);
    })
    .catch(console.error);

}, []);

  // ── DERIVE 3 CONTACTS from live data ──────────────────────────────────────
  // Engineer: always the `engineer` object from API
  // Supervisor: first stakeholder whose designation matches "supervisor/manager"
  // Applicator: first stakeholder whose designation matches "applicator/painter"
  // Fallback: if designation doesn't match, distribute by index order
const engineerContact = engineer || stakeholders.find(s => resolveRole(s.designation) === "engineer");
const finalSupervisor = stakeholders.find(s => resolveRole(s.designation) === "supervisor");
const finalApplicator = stakeholders.find(s => resolveRole(s.designation) === "applicator");
  // ── KPI counts ─────────────────────────────────────────────────────────────
  const totalContacts = (engineerContact ? 1 : 0) + stakeholders.length;
  // const available     = totalContacts; // all treated as available

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <DashboardShell
      portal="Client Portal"
      roleLabel="CLIENT"
      title="Client Workspace"
      subtitle="Track your project updates, reports and discussions in one place"
      navItems={clientNav}
      user={{ initials: userInitials, name: userName, role: userRole }}
    >
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
        .cp-root, .cp-root * {
          font-family: 'Montserrat', system-ui, sans-serif !important;
          box-sizing: border-box;
        }
        .cp-kpi-grid      { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; }
        .cp-contact-grid  { display: grid; grid-template-columns: repeat(3,1fr); gap: 20px; }
        .cp-header-row    { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:0.4; } }

        @media (max-width: 1100px) {
          .cp-kpi-grid     { grid-template-columns: repeat(2,1fr) !important; }
          .cp-contact-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 640px) {
          .cp-kpi-grid     { grid-template-columns: 1fr 1fr !important; }
          .cp-contact-grid { grid-template-columns: 1fr !important; }
          .cp-header-row   { flex-direction: column !important; }
        }
      `}</style>

      <div className="cp-root" style={{ display: "flex", flexDirection: "column", gap: 28 }}>

        {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
        <div className="cp-header-row">
          <div>
            {/* Eyebrow breadcrumb */}
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
              <div style={{ width:3, height:18, borderRadius:2, background:`linear-gradient(180deg,${T.engineer},${T.applicator})`, flexShrink:0 }} />
              <span style={{ fontSize:11, fontWeight:700, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase" }}>
                Client Portal · Contact Directory
              </span>
            </div>
            <h1 style={{ margin:0, fontWeight:800, fontSize:24, color:T.dark, letterSpacing:"-0.4px" }}>
              Project Contacts
            </h1>
            <p style={{ margin:"4px 0 0", fontSize:13, color:T.muted }}>
              Reach your site engineer, supervisor and applicator directly.
            </p>
          </div>

          {/* Live count chip */}
          <div style={{
            display:"inline-flex", alignItems:"center", gap:8,
            background:T.surface, border:`1px solid ${T.border}`,
            borderRadius:12, padding:"10px 16px",
            fontSize:13, fontWeight:600, color:T.body,
            boxShadow:"0 1px 4px rgba(0,0,0,0.05)", whiteSpace:"nowrap", flexShrink:0,
          }}>
            <span style={{
              width:8, height:8, borderRadius:"50%",
              background:T.success, display:"inline-block",
              animation:"pulse 2s infinite",
            }} />
            {totalContacts} Active Contact{totalContacts !== 1 ? "s" : ""}
          </div>
        </div>

        {/* ── KPI STRIP ───────────────────────────────────────────────────── */}
        <div className="cp-kpi-grid">
          <KpiChip icon={Users}        label="Total Contacts"  value={totalContacts}        color={T.engineer}   light={T.engineerLight}   />
          <KpiChip icon={Wrench}       label="Engineers"        value={engineerContact?1:0}  color={T.engineer}   light={T.engineerLight}   />
          <KpiChip icon={ClipboardList} label="Supervisors"      value={finalSupervisor?1:0}  color={T.supervisor} light={T.supervisorLight} />
          <KpiChip icon={Paintbrush}   label="Applicators"     value={finalApplicator?1:0}  color={T.applicator} light={T.applicatorLight} />
        </div>

        {/* ── ROLE LEGEND BAR ─────────────────────────────────────────────── */}
        <div style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          padding: "14px 20px",
          display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}>
          <span style={{ fontSize:11, fontWeight:700, color:T.faint, textTransform:"uppercase", letterSpacing:"0.08em", flexShrink:0 }}>
            Role Key
          </span>
          {Object.entries(ROLE_MAP).map(([key, cfg]) => (
            <div key={key} style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{
                width:28, height:28, borderRadius:8,
                background:cfg.gradient,
                display:"flex", alignItems:"center", justifyContent:"center",
                color:"#fff",
              }}><cfg.icon size={14} /></div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:T.dark }}>{cfg.label}</div>
                <div style={{
                  width:32, height:3, borderRadius:2,
                  background:cfg.gradient, marginTop:2,
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* ── 3-COLUMN CONTACT CARDS ──────────────────────────────────────── */}
        <div>
          <div style={{ marginBottom:18 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <div style={{ width:3, height:20, borderRadius:2, background:`linear-gradient(180deg,${T.engineer},${T.applicator})`, flexShrink:0 }} />
              <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:T.dark }}>On-Site Team</h2>
            </div>
            <p style={{ margin:"4px 0 0 13px", fontSize:13, color:T.muted }}>
              Your dedicated project team — contact them directly for updates.
            </p>
          </div>

          <div className="cp-contact-grid">
            {/* Engineer */}
            {engineerContact
              ? <ContactCard person={engineerContact}   roleKey="engineer"   />
              : <EmptyCard roleKey="engineer" />
            }
            {/* Supervisor */}
            {finalSupervisor
              ? <ContactCard person={finalSupervisor}  roleKey="supervisor" />
              : <EmptyCard roleKey="supervisor" />
            }
            {/* Applicator */}
            {finalApplicator
              ? <ContactCard person={finalApplicator}  roleKey="applicator" />
              : <EmptyCard roleKey="applicator" />
            }
        </div>
        </div>

      
      </div>
    </DashboardShell>
  );
}